import { OrderButtonProps } from "features/menu/components/OrderButton";
import { useCampaign } from "features/shared/hooks/useCampaign";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import { NavigationBase } from "shared/components/atoms/Navigation";
import { StarRating } from "shared/components/molecules/StarRating";
import { FreeUsage } from "shared/features/Menu/components/FreeUsage";
import { usePrice } from "shared/features/shared/usePrice";
import { zIndex } from "shared/styles";
import { numberToCurrency } from "shared/utils/numberToCurrency";
import tw, { styled } from "twin.macro";

const OrderButton = dynamic(
  async () => (await import("features/menu/components/OrderButton")).OrderButton
);

const blossomTheme = tw`bg-blossom text-theme`;

const strongTheme = tw`bg-[#FE4FD0] text-white bottom-[85px]`;

const secondaryTheme = tw`bg-gray text-secondary`;

const HighLight = styled.div`
  position: fixed;
  width: 100%;
  height: 30px;
  bottom: 66px;
  font-size: 10px;
  font-weight: 600;
  z-index: ${zIndex.fixed};
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 640px;
`;

export const MenuNav = ({
  menu,
  onSuccessSubscribeNotification,
}: OrderButtonProps) => {
  const { getOriginalPrice } = usePrice();
  const { coupon, showCampaignPromotion } = useCampaign({
    fortuneTellerId: menu?.fortuneTeller.id,
  });

  const isTel = menu?.type === "tel";
  const originPrice = getOriginalPrice(menu);
  const highlightTheme = useMemo(() => {
    if (menu?.fortuneTeller.isBusy) {
      return blossomTheme;
    } else if (isTel && showCampaignPromotion) {
      return strongTheme;
    } else {
      return secondaryTheme;
    }
  }, [menu?.fortuneTeller.isBusy, isTel, showCampaignPromotion]);

  const highlightText = useMemo(() => {
    const text1 = menu?.fortuneTeller.isBusy ? "鑑定中" : "受付一時停止中";
    const text2 = menu?.isSetNotify
      ? "受付再開通知をお待ちください😣"
      : "受付再開通知を設定してお待ちください😣";

    if (menu?.type === "chat") {
      return `現在、${text1}です。${text2}`;
    } else {
      if (!menu?.fortuneTeller.telAcceptFlag) {
        return "現在、受付一時停止中です。受付再開をお待ちください😣";
      } else if (showCampaignPromotion) {
        return `お持ちのクーポン利用で最大${
          coupon?.discountAmount || 2000
        }円分無料`;
      }
    }
  }, [menu, showCampaignPromotion, coupon?.discountAmount]);

  const showHighlight = useMemo(() => {
    if (menu?.type === "chat") {
      return menu?.fortuneTeller.isBusy || !menu?.fortuneTeller.chatAcceptFlag;
    } else {
      return !menu?.fortuneTeller.telAcceptFlag || showCampaignPromotion;
    }
  }, [menu, showCampaignPromotion]);

  const unit = menu?.type === "chat" ? "鑑定" : "分";
  const footerTitle =
    isTel && showCampaignPromotion ? (
      <div>
        <span tw="line-through text-secondary text-2xs font-normal">
          通常時 {`${numberToCurrency(originPrice)} / ${unit}`}
        </span>
        <FreeUsage />
      </div>
    ) : (
      `${numberToCurrency(originPrice)} / ${unit}`
    );

  return (
    <>
      {showHighlight && (
        <HighLight css={highlightTheme}>{highlightText}</HighLight>
      )}
      <NavigationBase
        tw="h-[66px] items-center py-3 px-5"
        css={isTel && showCampaignPromotion && tw`h-[85px]`}
      >
        <div tw="w-[45%] flex flex-col">
          <div
            tw="font-base text-[15px] tracking-[.44px] leading-[15px] mb-2"
            css={isTel && showCampaignPromotion && tw`mb-1`}
          >
            {footerTitle}
          </div>
          <div tw="flex">
            <StarRating
              rate={menu?.averageScore}
              showAverage={false}
              size="large"
            />
            <span tw="font-base font-light text-xs leading-3 tracking-[.3px] text-secondary ml-1">
              ({menu?.reviewCounts})
            </span>
          </div>
        </div>
        <div tw="flex-[55%] flex flex-col">
          {menu && (
            <OrderButton
              menu={menu}
              onSuccessSubscribeNotification={onSuccessSubscribeNotification}
            />
          )}
        </div>
      </NavigationBase>
    </>
  );
};
